import { memo } from 'react'
import * as Styled from '../styled'
import { ITextData, TMediaItemType } from 'types/media'
import { imageTypes, videoTypes } from 'consts'
import { calculateShownVideoFrames } from './utils'
import { useGetThumbLoad } from './useGetThumbLoad'
import { isTextMediaItem } from 'utils'
import { ITimelineMediaFrame } from '../../hooks/use-media-item-frames'

interface IThumbnailContentProps {
  type: TMediaItemType
  thumbnail: string | null
  videoThumbnailWidth: number
  videoThumbnailLeft: number
  framesByTime: ITimelineMediaFrame[]
  frameSpacing: number
  pixelsPerSecond: number
  trimStart: number
  textData?: ITextData
}

export const ThumbnailContent = memo(
  ({
    type,
    thumbnail,
    videoThumbnailWidth,
    videoThumbnailLeft,
    framesByTime,
    frameSpacing,
    pixelsPerSecond,
    trimStart,
    textData
  }: IThumbnailContentProps) => {
    const isText = isTextMediaItem(type)

    const isVideoThumb = videoTypes.includes(type) && framesByTime.length > 0
    const isImageThumb = imageTypes.includes(type) && Boolean(thumbnail)

    const isThumbLoading = useGetThumbLoad(isImageThumb ? thumbnail : null)

    if (isText) {
      return (
        <Styled.TextThumbnail $fillColor={textData?.fillColor}>
          <Styled.TextThumbnailText>
            {textData?.text || 'Text'}
          </Styled.TextThumbnailText>
        </Styled.TextThumbnail>
      )
    }

    const shownFrames = calculateShownVideoFrames(
      framesByTime,
      videoThumbnailWidth,
      pixelsPerSecond,
      frameSpacing,
      trimStart
    )

    if (isVideoThumb) {
      return (
        <Styled.VideoThumbnail
          $width={videoThumbnailWidth}
          $left={videoThumbnailLeft}
        >
          {shownFrames.map((frame, index) =>
            frame ? (
              <Styled.VideoFrame
                src={frame}
                crossOrigin="anonymous"
                width={pixelsPerSecond * frameSpacing}
                // eslint-disable-next-line react/no-array-index-key
                key={`${frame}-${index}`}
              />
            ) : (
              <Styled.VideoFrameSkeleton
                // eslint-disable-next-line react/no-array-index-key
                key={`skeleton-${index}`}
                $width={pixelsPerSecond * frameSpacing}
              />
            )
          )}
        </Styled.VideoThumbnail>
      )
    }

    if (isImageThumb) {
      return (
        <>
          {isThumbLoading && <Styled.ThumbnailSkeletonBg />}
          <Styled.Thumbnail
            $bgImageUrl={thumbnail!}
            $isThumbLoading={isThumbLoading}
          />
        </>
      )
    }

    return <Styled.ThumbnailSkeletonBg />
  }
)

ThumbnailContent.displayName = 'ThumbnailContent'
